import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { requiredValidator, urlValidator } from "components/Form/Form.utils";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import { normalizeNullableStringFields } from "config/resourceUtils";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import { makeTestConnectionActionSignature } from "containers/Resources/components/CommonActions/testConnectionAction";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import {
    buildSimpleColumn,
    isEnabledColumn,
} from "containers/Search/components/SearchTabs/columns";
import resource from "./resource";
import { getUrlDTO, isEnabledField } from "./sharedFields";

const devolutionsDocsLink = "/docs/vault-integration#devolutions-server";

const resourceName = "devolutions_config";

const config: ResourceConfig = {
    label: "Devolutions config",
    icon: IconCollection.safeSquareOutline,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, metadataTab.tab, commentsTab.tab],
                content: [logsTab.content, metadataTab.content, commentsTab.content],
            },
        },
    ],
    actions: {
        shown: [makeTestConnectionActionSignature({ resourceName })],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            is_enabled
            devolutions_url
            verify_ssl
            vault_id
            cacert
            app_key
            has_app_secret
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            devolutions_url
            has_app_secret
            verify_ssl
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $is_enabled: Boolean,
            $verify_ssl: Boolean,
            $devolutions_url: String,
            $vault_id: String!,
            $cacert: String,
            $app_key: String,
            $app_secret: String,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            is_enabled: $is_enabled,
            verify_ssl: $verify_ssl,
            devolutions_url: $devolutions_url,
            vault_id: $vault_id,
            cacert: $cacert,
            app_key: $app_key,
            app_secret: $app_secret,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $is_enabled: Boolean,
            $verify_ssl: Boolean,
            $devolutions_url: String,
            $vault_id: String,
            $cacert: String,
            $app_key: String,
            $app_secret: String,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            is_enabled: $is_enabled,
            verify_ssl: $verify_ssl,
            devolutions_url: $devolutions_url,
            vault_id: $vault_id,
            cacert: $cacert,
            app_key: $app_key,
            app_secret: $app_secret,
        `,
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Devolutions URL",
            key: "devolutions_url",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    getUrlDTO(value.devolutions_url),
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "devolutions_url",
                                  value: value.devolutions_url,
                              })
                        : undefined
                ),
        },
        {
            label: "Has app secret",
            key: "has_app_secret",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                value.has_app_secret
                    ? buildSimpleColumn(
                          <Icon icon={IconCollection.check} />,
                          setNewValue
                              ? () => setNewValue({ field: "has_app_secret", value: true })
                              : undefined
                      )
                    : null,
        },
        {
            label: "Verify SSL",
            key: "verify_ssl",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="verify_ssl"
                        value={value.verify_ssl}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () => setNewValue({ field: "verify_ssl", value: value.verify_ssl })
                        : undefined
                ),
        },
    ],
    form: [
        isEnabledField,
        {
            name: "devolutions_url",
            component: "url",
            fieldProps: {
                label: "Devolutions URL",
                docs: {
                    text: "URL of Devolutions Server",
                    url: devolutionsDocsLink,
                },
            },
            validation: urlValidator,
        },
        {
            name: "cacert",
            component: "textarea",
            fieldProps: {
                label: "CA certificate",
                docs: {
                    text: "Self Signed Certificate",
                    url: devolutionsDocsLink,
                },
            },
        },
        {
            name: "verify_ssl",
            component: "switch",
            fieldProps: {
                label: "Verify SSL",
                docs: {
                    text: "Verify the server's SSL certificate. **Strongly recommended**. Can be disabled if using a self-signed certificate.",
                    url: devolutionsDocsLink,
                },
            },
        },
        {
            name: "vault_id",
            component: "text",
            fieldProps: {
                label: "Vault ID",
                docs: {
                    text: "The ID of the Devolutions vault",
                    url: devolutionsDocsLink,
                },
            },
            fieldAttr: {
                required: true,
            },
            validation: requiredValidator,
        },
        {
            name: "app_key",
            component: "text",
            fieldProps: {
                label: "App key",
                docs: {
                    text: "A Devolutions appKey which is used to authenticate",
                    url: devolutionsDocsLink,
                },
            },
        },
        {
            name: "app_secret",
            component: "writeOnlyInput",
            fieldProps: {
                label: "App secret",
                clearable: false,
                docs: {
                    text: "A Devolutions appSecret which is used to authenticate",
                    url: devolutionsDocsLink,
                },
            },
            fieldAttr: {
                placeholder: "Set new app secret",
            },
        },
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) =>
            normalizeNullableStringFields(resourceData, ["cacert"]),
    },
};
export default config;
